import { ArticleItem } from 'types'

const textData: Array<ArticleItem> = [
  {
    text: 'IMPROVISATIONS GAMES:',
    style: ['title1'],
  },
  {
    text: 'Possibilities for collective initiation to the cello',
    style: ['title1'],
  },
  {
    text: 'Author: Marta Macedo Brietzke',
    style: ['paragraph', 'margin'],
  },
  {
    text: 'Supervisor: Prof. Dr. Fabio Soren Presgrave',
    style: ['paragraph', 'margin'],
  },
  {
    text: 'Supervisor: Prof. Dr. Mário André Wanderley Oliveira',
    style: ['paragraph', 'margin'],
  },
  {
    text: '',
    style: ['space'],
  },
  {
    text:
      "This paper is part of Marta Macedo Brietzke's Master Thesis entitled “Contemporary Music in collective initiation to the cello: an action-research with improvisation games in three educational institutions in the state of São Paulo”, defended on December 12th 2018 through the University of São Paulo, with supervision from Prof. Dr. Fabio Soren Presgrave and co-supervision from Prof. Dr. Mário André Wanderley Oliveira.",
    style: ['paragraph', 'italic'],
  },

  {
    text: 'The Contemporary Music:',
    style: ['title2', 'anchor'],
  },
  {
    text:
      'The term contemporary music, widely used by performers, teachers and students of music, still lacks a precise definition since one of the problematics that involves the use of this term is the idea that it would be intended to define all and any music produced nowadays. To help us to define what commonly called in academia by contemporary music, we resorted to the conceptions of the professor Bernardete Zagonel. The professor elucidates that, in those circles, the expression often used to refer to the European classical music of the XX and XXI centuries, which has been aesthetically oriented by profound philosophical, scientific, cultural and technological changes that have taken place during this period. When we are willing to listen to and appreciate contemporary music, we must detach ourselves from old paradigms and open our perceptions to understand and apprehend other forms of musical expression. So, contemporary music can be defined as the music production connected to classical music, linked to the artistic proposals formulated from the twentieth century on and marked by the complexities of various artistic, cultural and philosophical references.',
    style: ['paragraph'],
  },
  {
    text:
      'Although these new characteristics have been consolidated in different ways since mid-twentieth century, even nowadays, commonly, a fraction of the professional musicians and music students is not familiar and even does not like to play or listen to music that involves sounds possibilities different from those considered traditional (those produced in the previous centuries). However, since the last century distincts musicians, composers, educators and instrument teacher, seek to embody this repertoire and sound possibilities to the practices of students. As an example of these initiatives, we can mention the works of Béla Bartók and Boris Porena, as also of the brazilians Heitor Villa-Lobos, Ernest Mahle and Bruno Kiefer.',
    style: ['paragraph'],
  },
  {
    text:
      'The insertion of the contemporary music repertoire in the instrument classes continues to be reduced and poorly publicized. The low acceptance of this repertoire in the concert halls across the country may also raise doubts about the importance of its application, mainly among students of beginner levels. We must comprehend that the application of proposals related to the poetics of contemporary music brings students closer to the conceptions and thought of their own time, especially when these proposals are linked to activities of creation, such as what happens in the practices of improvisation. By using the term “poetic” to refer to contemporary music, we come close to a conception that sees music not only as language but as an artistic expression grounded on the philosophical, aesthetic, historical, social and political conceptions of its surroundings and, even in biographical elements of artists. This term, used since antiquity by the Greek philosopher Aristotle, to refer to literary works, has been adopted in contemporaneity to refer to other artistic manifestations, such as the visual arts, the performing arts and music, referring to everything that is produced making deliberate use of an aesthetic composition.',
    style: ['paragraph'],
  },
  {
    text:
      'Through the proposals of contemporary music that involve improvisation, we bring the creation into the instrument classrooms and bring the art of everyday life closer, and the "music- thought", as the poet Augusto de Campos called it, from the day to day of students/ performers. Currently, "music-thought", or "invention music", as it is sometimes named, seems to be aimed at a minimal part of the population that has access to specialized music education. Thus, the openness to different musical manifestations, which are understood as socio-cultural phenomena, seems to be conditioned to factors of a social and economic nature, undemocratic and not very inclusive. In Brazil, musical education in specialized schools, frequently is intended almost exclusively to investigate and reproduce repertoires from centuries before the 20th and 21st centuries. Thereby, the student tends to perform only works of the past, developing neither his creative power nor his critical and reflective capacity. In the main Brazilian concert halls, the musical presentation also seems to be addressed often to the appreciation of historical character, being the idea of art as a promoter of critical thinking and reflection undervalued.',
    style: ['paragraph'],
  },
  {
    text:
      'It is up to us, music teachers, together with composers and researchers in the area, to highlight the value of music as a product and producer of experiences and thought. Therefore, the implementation of current musical works, since the beginning of the formation of the student / performer is essential so that we can bring the "music-thinking" into our daily life.',
    style: ['paragraph'],
  },
  {
    text: 'The improvisation:',
    style: ['title2', 'anchor'],
  },
  {
    text:
      'Improvisation is a practice that has developed over the centuries and in different ways in many cultures, such as Indian, Arab and Persian cultures, among others. In European cultures, this practice was common until the middle of the 19th century, when the roles of composer and performer began to be outlined as self-excluding. The practice of improvisation continued to be addressed, generally in the midst of popular music, in styles such as jazz, choro, salsa, in artistic manifestations known as "free improvisation", as well as in pieces by composers of contemporary poetics that, sometimes, earmark passages to performers\' improvisation.',
    style: ['paragraph'],
  },
  {
    text:
      'Since the mid-twentieth century, the practice of improvisation has also proven to be an important pedagogical strategy for the teaching of music. However, this practice is rarely applied in musical instrument classes, according to the researcher Chefa Alonso, with the exception of the Nordic countries. Understanding the importance of activities related to improvisation and some proposals for the use of this practice helps us, as teachers, to elaborate concepts where this is included in the planned activities for our classes. Researchers and music educators emphasize in their work and reports that, through activities related to improvisation, it is possible to present the musical contents to students, while music becomes a vehicle for the expression of perceptions, thoughts and feelings.',
    style: ['paragraph'],
  },
  {
    text:
      'Since the work of researcher Derek Bailey, which dates back to 1993, improvisation began to be classified according to some characteristics related to his idiom. Although the use of these terms has been relativized by the researcher who defined them, they are still widely used. It is called "idiomatic improvisation" which is linked to a specific style or musical "language" and "non-idiomatic improvisation" which does not relate to a particular language, but to sound, with its possible relations and transformations. Among the practices related to "non-idiomatics" is also the "free improvisation," which encompasses a series of manifestations that define themselves as such. In common, these practices develop a focus on performance, which includes, to a greater or lesser extent, an active and creative performance in real time. Musical educators, in most cases, address in their teaching and learning processes some elements of proposals linked to free improvisation.',
    style: ['paragraph'],
  },
  {
    text:
      'The practice of free improvisation as an artistic object emerged in Germany and England in the sixties of the twentieth century, through the creation of several groups dedicated à to this practice. In parallel it began to be observed by musical educators that children naturally already improvise freely and that if this practice is stimulated, musical elements can be incorporated into their experiences and learning. The educators emphasize some of the benefits of improvisation, among them, the learning of new forms of interpersonal relationships based on equality, the deepening of the relationship with the instrument, the greater understanding of musical compositions, the need to solve problems that present themselves as similar to those of everyday life, the development of creativity, self-expression, self-esteem and autonomy.',
    style: ['paragraph'],
  },
  {
    text: 'The gaming in learning:',
    style: ['title2', 'anchor'],
  },
  {
    text:
      'Educators and thinkers from several areas have been developing through time, ideas about learning through games, however, researchers today consider that these ideas are not yet clear to all people who work directly with education. One of the reasons for this confusion concerns the polysemic characteristic of the word game, which can be used to refer to activities with different meanings. The game is been studied as a natural and essential part of the human being, an essential part of learning and the transmission of knowledge and of culture itself.',
    style: ['paragraph'],
  },
  {
    text:
      'For researchers who deal with learning through games, these do not have a specific "educational goal", but consider that everything we learn, we learn through the game. The improvisation games proposed by this paper follow this idea, which they do not have as objective the development of specific content, but are intended to work the innate playful nature of children and young people.',
    style: ['paragraph'],
  },
  {
    text: 'Collective initiation to the cello:',
    style: ['title2', 'anchor'],
  },
  {
    text:
      "In our practice as teachers, we often find institutions where teaching takes place only in an individual way, or tutorial, and others where teaching takes place collectively. In Brazil, mostly collective education takes place in institutions or NGO's where the flow of students is constant and it is necessary to attend to a large number of students at the same time, while in free or conservatory schools, teaching is directed to a single person at a time. However, the choice of collective education goes beyond economic constraints, taking into account its objectives and conceptions.",
    style: ['paragraph'],
  },
  {
    text:
      'It is estimated that group instrument teaching began in the 19th century in England and later in the United States. In Brazil, it began to be embraced in the fifties of the twentieth century, and in the seventies, a great movement emerged for the collective teaching of stroke strings, headed by Alberto Jaffé and Dayse de Luca. After the dissemination of the collective teaching instrument, researchers began to investigate the benefits of such teaching method, benefits that, in addition to musicals, are psychological and social in nature.',
    style: ['paragraph'],
  },
  {
    text:
      "In collective teaching, it is believed that it is possible to share knowledge, space, and that interaction and diversity are important parts of learning. In addition to these values, the musical practice collectively provides greater diversification of musical contents approached as they include some variables that are impossible in individual teaching. It is worth mentioning that collective education should not be a copy of individual education, only directed to more people at the same time. One of the features of the collective teaching is that the students must be busy during the entire class. To do so, the teacher's performance should also be differentiated, able to gather all students in function of the proposed objectives. The gaming improvisation practices offered on this paper are only possible within this context, given that they are directed to the collective learning of the instrument in association with the objectives and conceptions of this process.",
    style: ['paragraph'],
  },
  {
    text: 'Practical activities:',
    style: ['title2', 'anchor'],
  },
  {
    text:
      "The educator, researcher and composer François Delalande, member of the French group GRM, founded by Pierre Schaeffer in 1951, dedicated to concrete and electroacoustic music, through intense research with children, points out that, commonly, when starting to study a musical instrument, students have a similar posture to the composer of concrete music, in other words, the exploratory need with expressive purposes of the sound bodies, in our case, the cello. As teachers, we can observe this need in the student's natural tendency to impact, scrape, stroke and produce different actions with different sound results. We can also observe that children and teens, in the initial phase of the study of the cello, do not have aesthetic prejudices such as those presented by students or musicians more technically advanced. Beginner students accept the cello as an infinite possibility of producing different music, with its aesthetic multiplicity. Such observation leads us to the conclusion that the difficulties of understanding contemporary music by professional musicians is a difficulty posed by the systematic repetition of certain musical styles, generally those belonging to European concert music of the 18th and 19th centuries.",
    style: ['paragraph'],
  },
  {
    text:
      'Thus, it can be concluded that one of the ways to start teaching the contemporary music repertoire and improvisation games is to allow the student to exercise his natural curiosity in exploring the sounds of the surroundings, among them, the sounds that can be produced by the cello. In doing so, we can use some ideas from some composers of contemporary music, such as the narrative, soundscape and mimesis. A mimesis is a resource used by different artistic languages, including music. In music, themimesisbasically consists of the imitation of environmental or psychological phenomena through the qualities of sound and the construction of musical narratives.',
    style: ['paragraph'],
  },
  {
    text:
      'During the cello exploration, the mimesis can also be used as a pedagogical resource. The several sounds produced by the cello can be related, along with the students, to psychological and environmental contents. Such approach provides students with the understanding that the sounds produced by the extended techniques, i.e., unconventional ways of approaching the instrument, are not only noises, but an important part of the expressive musical range, and students start to produce musical content increasingly subjective, encompassing both conventional techniques and extended techniques. In such way, they are able to express their thoughts, feelings and emotions through musical ideas.',
    style: ['paragraph'],
  },
  {
    text: '',
    style: ['line'],
  },
  {
    text: 'The collective improvisation games:',
    style: ['title1'],
  },
  {
    text: 'Description of Game 1',
    style: ['paragraph', 'bold', 'anchor'],
  },
  {
    text: 'Question and Answer with open strings in pizzicato',
    style: ['title2'],
  },
  {
    text: 'Author: Marta Brietzke',
    style: ['paragraph', 'italic'],
  },
  {
    text: 'Objectives:',
    style: ['paragraph', 'bold'],
  },
  {
    text:
      "Provide awareness to the students of their individuality and their role within the collective, establish the idea of musical narrative, questions and answers, emphasizing the use of the violoncello's four strings, the pizzicato, and the different rhythms, metrics, timbres and dynamics.",
    style: ['paragraph'],
  },
  {
    text: 'Methodology and application:',
    style: ['paragraph', 'bold'],
  },
  {
    text:
      'The game begins by explaining to the students what could be a question and an answer within a musical "narrative". We can give an example with our own instrument, using only open strings. At this point, a good strategy for students to understand the concepts of question and answer is to use variations in dynamics or rhythm.',
    style: ['paragraph'],
  },
  {
    text:
      'We then propose that each of the students prepare a musical answer to a question that will be asked by us. The teacher plays a question and the first student plays a answer. The teacher then plays another question and the second student plays an answer and consecutively, until all the students have played the game. We can have as many rounds as we deem it necessary, depending on each class or group of students. Questions can encompass different dynamics, rhythms and metrics, in order to stimulate students in different ways.',
    style: ['paragraph'],
  },
  {
    text:
      'After that, we propose that the students split into pairs and choose who will ask the question and who will answer it. Each pair is arranged in one spot in the room and it is important that the students have experienced both roles. The idea is that after this moment, the students can "present", in pairs, to the other students a series of questions and answers elaborated by them.',
    style: ['paragraph'],
  },
  {
    text:
      'It is important that we allow the students a certain autonomy. It is interesting that we let at their choice define how many questions and answers they want to present to their classmates, who starts the questions, what sign they will agree on for the beginning and the end of the question and answer sessions. Frequently, we will observe an increase in the complexity of the questions and answers asked by students compared to those elaborated at the beginning of the game .',
    style: ['paragraph'],
  },
  {
    text: 'Source: elaborated by the author.',
    style: ['paragraph', 'citation'],
  },
  {
    text: '',
    style: ['line'],
  },
  {
    text: 'Description of Game 2',
    style: ['paragraph', 'bold', 'anchor'],
  },
  {
    text: 'Green Light X Red Light',
    style: ['title2'],
  },
  {
    text: 'Author: Maria Teresa Alencar de Brito/Murray Schafer',
    style: ['paragraph', 'italic'],
  },
  {
    text: 'Objectives:',
    style: ['paragraph', 'bold'],
  },
  {
    text:
      'Work on the differences between sound and silence, as well as its importance and role within the musical construction; deepen the importance of collectivity within the musical process; to establish the idea of musical dialogue.',
    style: ['paragraph'],
  },
  {
    text: 'Methodology and application:',
    style: ['paragraph', 'bold'],
  },
  {
    text:
      'Before its realization, we can propose to the students to explore the different sounds of their cellos. One one of the ways to carry out this practice is through imitation, we propose a sound and ask the students to repeat, together or individually, that same sound. It is important that we name the techniques that already have a name defined by the literature, such as con legno, sul ponticelo, pizzicato Bartók. It is also important for us to make room for students to discover their own sounds, dedicating a part of this exploration to them so they can explore their instruments on their own. We can ask each of the students to show the other students a sound they have discovered, in order to give the student once again, a voice and then everyone repeats that sound.',
    style: ['paragraph'],
  },
  {
    text:
      'We can also choose some of these sounds and share them with students if we consider that learning all the sounds on the same day can cause some assimilation difficulty for students. Posteriorly, in the next classes, we can introduce some more of these sounds, in order to increase the students\' expressive range. In some groups of students who do not yet know how to hold the bow, we can offer these same sounds, proposing that students hold the bow by "hugging" it on the frog. In doing so, students will be comfortable and will not acquire postural vices. Most sounds can be produced with the bow held this way, allowing the games to be developed in parallel with the students\' technical acquisition.',
    style: ['paragraph'],
  },
  {
    text:
      'After exploring the instrument, we will show the students two small posters, one green and one red. We associate these posters with traffic rules, where green means to go or play, and red means to stop or do not play. We can propose that the students choose, each one of them, one of the sounds previously inventoried, or that they produce different sounds. When the green light is displayed, students should play a sound on their cello, when the red light is displayed, students should take a pause. In that way, we establish the importance and function of silence in the musical perspective.',
    style: ['paragraph'],
  },
  {
    text:
      'It is interesting to provide students with the experience of being the "conductor", i.e., the person who will choose when sound is made and when there is silence. After a few rounds of the game, we can give out one more green and one red signal, establishing two "conductors" and dividing the group in two. Each group should follow the orientations of its specific conductor, which will result in musical dialogues between the two groups and a more complex sonority.',
    style: ['paragraph'],
  },
  {
    text:
      'The conductors must communicate with each other to define the beginning and the end of the song, and we must establish that this communication must be non-verbal. In this phase of the game, it is important that both the conductors and the performers listen to the different sounds produced by the group and become aware of the musical "narrative" that is being created. Conductors should be encouraged to create, along with the green and red signals, signs of dynamic variation, articulation and character. For example, to lower the signal may mean playing more piano,raising the signal may mean playing louder. We must be attentive and encourage that the students\' listening ability is deepened in each round of the game, avoiding that it becomes mechanical and repetitive.',
    style: ['paragraph'],
  },
  {
    text:
      "It's also interesting at this moment that we show the recording of some musical play that uses extended techniques, such as, the play Pression by Helmut Lachenmann, avaliable in several versions on youtube. Such listening brings the work done by students closer to the work done by other people, which is important for students to value their own production and those from others. Another interesting piece is the soundpainting improvisations played by the group of Argentine cellists ARRE!. Some of these improvisations are also available on youtube and they allow the student to listen to different sonorities, being produced by group of cello, and as in Lachenmann, they approximate the work done by students with what is being produced by musicians of today.",
    style: ['paragraph'],
  },
  {
    text:
      'Source: Elaborated by the author, based on the proposals by Maria Teresa Alencar de Brito and Murray Schafer.',
    style: ['paragraph', 'citation'],
  },
  {
    text: '',
    style: ['line'],
  },
  {
    text: 'Description of Game 3',
    style: ['paragraph', 'bold', 'anchor'],
  },
  {
    text: 'Night at the Castle',
    style: ['title2'],
  },
  {
    text: 'Author: Claudia Freixedas',
    style: ['paragraph', 'italic'],
  },
  {
    text: 'Objectives:',
    style: ['paragraph', 'bold'],
  },
  {
    text:
      'Establish a relationship between the imagined and the sound; reinforce the trust between the pairs; develop the power of choice within the musical creation.',
    style: ['paragraph'],
  },
  {
    text: 'Methodology and application:',
    style: ['paragraph', 'bold'],
  },
  {
    text:
      'In this game, we tell a story, imagining a haunted castle in the middle of a stormy night.We encourage students to narrate what elements would be present in this castle, such as spider webs, bats, ghosts, as well as the elements of nature, such as thunder, gusts of wind, rain.In this storm, a traveler will come close to the castle and make the contact with these different manifestations.',
    style: ['paragraph'],
  },
  {
    text:
      'We place students in different spots of the room, preferably with a certain distance between them. Each student will choose the sound they want to play or it can be established that the students can play more than one type of sound, depending on what they think is appropriate for the musical context. Each student will represent an object of the castle, and it is not necessary to name each one of these objects and leaving it to the imagination of each one of them, giving possibilities for subjective associations to be made by the students.',
    style: ['paragraph'],
  },
  {
    text:
      'One student chosen by the group will be the traveler. This student will be blindfolded with simulating the darkness of the castle. The objective is for the traveler to walk through the castle without touching any object. To do so, when the traveler approaches an object, the student responsible for the object must produce the chosen sound. When the traveler moves away from the object, the sound decreases in intensity and the sound of the closest object to the traveler is triggered.',
    style: ['paragraph'],
  },
  {
    text:
      'As we run the rounds of that game, we can add complexities. We can suggest that the traveler takes a longer time in front of an object whose sound he wants to hear or that he returns to a sound he has already heard and that he wants to hear again. The student traveler then becomes a kind of conductor and his choices produce a certain musical "narrative".',
    style: ['paragraph'],
  },
  {
    text:
      'It is interesting to make an audio or video recording of the improvisation and then listen to this recording together and discuss the result. Listening to the entire piece gives all participants a general idea of the musical proposal produced. Sometimes, students involved in the performance can lose track of the musical as a whole and this listening is fundamental for students to realize that they are not just producing disconnected noises, but a musical "path".',
    style: ['paragraph'],
  },
  {
    text: 'Source: Elaborated by the author, based on the proposals by Claudia Freixedas.',
    style: ['paragraph', 'citation'],
  },
  {
    text: '',
    style: ['line'],
  },
  {
    text: 'Description of Game 4',
    style: ['paragraph', 'bold', 'anchor'],
  },
  {
    text: 'Musical signature',
    style: ['title2'],
  },
  {
    text: 'Author: Wanderson Cruz',
    style: ['paragraph', 'italic'],
  },
  {
    text: 'Objectives:',
    style: ['paragraph', 'bold'],
  },
  {
    text:
      'Enable the experience of creation from pre-established musical cells; expose the differences between the polyphonies and homophonies; encourage the power of choice within musical creation.',
    style: ['paragraph'],
  },
  {
    text: 'Methodology and application:',
    style: ['paragraph', 'bold'],
  },
  {
    text:
      'We start by asking students what a signature is. Soon after, we related that answer to what would be a "musical signature". We can give an example with our own instrument, and it is interesting that we use conventional sounds and sounds from the extended techniques.',
    style: ['paragraph'],
  },
  {
    text:
      "We then propose to the students that each one creates their own musical signature using the sounds they want. We give students time to do this research and individually choose their signature. Afterwards, we propose that each student execute his or her signature for the other students in the group, so that everyone can know all the signatures. It is worth asking students to execute their signature more than once, so that we can check whether they are always keeping the same signature or whether they are executing a different signature each time. For this game, we propose that each student's signatures should always be the same.",
    style: ['paragraph'],
  },
  {
    text:
      "After the students' presentation, we suggest the composition of a piece using the musical signatures of each student. We choose a conductor to organize the composition. The conductor performs signals with his hands and body indicating when each student should play his signature, with what dynamics and when to stop playing. The idea is that until the conductor gives a signal to stop playing, the signatures are executed continuously. When the student's subscription ends, he or she restarts from the top until he or she receives a stop sign.",
    style: ['paragraph'],
  },
  {
    text:
      'We should stimulate the students to work on their listening and artistic choice in front of the material presented by their classmates. Thus, we again prevent the game from becoming mechanical and provide for the construction of a consistent musical idea.',
    style: ['paragraph'],
  },
  {
    text:
      'It is advisable to the teacher be the conductor in all games played for the first time, however, different students should be assigned for this role next, so everyone can take turns as conductors in any improvisation game.',
    style: ['paragraph'],
  },
  {
    text: 'Source: Elaborated by the author, based on the proposals by Wanderson Cruz.',
    style: ['paragraph', 'citation'],
  },
  {
    text: '',
    style: ['line'],
  },
  {
    text: 'Description of Game 5',
    style: ['paragraph', 'bold', 'anchor'],
  },
  {
    text: 'Same X Similar X Different',
    style: ['title2'],
  },
  {
    text: 'Author: Violeta Gainza',
    style: ['paragraph', 'italic'],
  },
  {
    text: 'Objectives:',
    style: ['paragraph', 'bold'],
  },
  {
    text:
      'Work on ideas of similarity and difference in music; facilitate the creation from a element previously given; work the idea of phrases or musical narratives.',
    style: ['paragraph'],
  },
  {
    text: 'Methodology and application:',
    style: ['paragraph', 'bold'],
  },
  {
    text:
      'We present three small posters. In the first poster, we present the mathematical sign used for equal, in the second the mathematical sign used for different and in the third poster we can create some sign that represents approximately equal to sign. We propose to students that they should respect the signs that will be presented to them. The first student freely creates a musical phrase, then the teacher presents one of the signs to the second student, who must repeat or modify the phrase according to the sign shown. The teacher then gives a new signal to the third student, who must now repeat or modify the phrase presented by the second student, and successively so, until all students have elaborated their phrases. The signs can be chosen randomly or previously selected by the teacher.',
    style: ['paragraph'],
  },
  {
    text:
      'Next, we propose to have one of the students as the conductor and give the indications on the posters to their classmates. We can add complexities to the game by proposing that the conductor, in addition to choosing the next poster he or she will be giving to other students, also choose, always in a non-verbal way, the student who will perform the next variation of the phrase.',
    style: ['paragraph'],
  },
  {
    text: 'Source: Elaborated by the author, based on the proposals by Violeta Gainza.',
    style: ['paragraph', 'citation'],
  },
  {
    text: '',
    style: ['line'],
  },
  {
    text: 'Description of Game 6',
    style: ['paragraph', 'bold', 'anchor'],
  },
  {
    text: 'Composition with images and novel notations',
    style: ['title2'],
  },
  {
    text: 'Author: Various authors/ Marta Brietzke',
    style: ['paragraph', 'italic'],
  },
  {
    text: 'Objectives:',
    style: ['paragraph', 'bold'],
  },
  {
    text:
      'Bringing students closer to the idea of composition; developing their own symbology that represents their musical ideas; making contact with different ways of writing and musical representation; paying attention to the existing limitations in any type of musical writing; bringing together composers and performers.',
    style: ['paragraph'],
  },
  {
    text: 'Methodology and application:',
    style: ['paragraph', 'bold'],
  },
  {
    text:
      'For the game of composition with engravings, images we requested in the previous class that students bring pictures from magazines or personal photos. However, it is interesting that the teacher brings some previously selected pictures, as sometimes the students forget to bring them and the activity is compromised. It is also required that the teacher bring cardboard posters and sets of colored pencils. We can also add other materials according to our availability, such as toothpicks, colored ribbons, strands of different thicknesses, stones, etc.',
    style: ['paragraph'],
  },
  {
    text:
      'We start the game by splitting the class in groups. Each group will present their images and photos to the others and the teacher may distribute more images to the students. We then suggest that students choose one or more engravings or photos, as many as they like, so that they can tell a musical story. We can give an exemple with our own chello choosing one of the images. We should emphasize that the story must have a storyline, a beginning, middle and end and that the students must discuss and elaborate this storyline together, according to the picture(s) they choose.',
    style: ['paragraph'],
  },
  {
    text:
      'After the storyline has been written, we encourage students to use their cellos to produce musical contents that represent the story they have created, encouraging them to tell the story in parts, or even simultaneously, depending on the choices made by the students. The selection of these contents should be made by the groups together and we can give them as much time as we deem necessary for them to research and select the desired sounds.',
    style: ['paragraph'],
  },
  {
    text:
      'In the third part of the game, we distribute the posters, colored pencils and other materials and propose that the students develop a way to "write" the music they have invented. This writing can use drawings, colors, symbols, words, depending on what each group finds convenient. We may suggest that you choose a title for their play, put their names as composers, or we may not even mention these aspects, giving each group the freedom to determine what they think is necessary to understand the play.',
    style: ['paragraph'],
  },
  {
    text:
      'When the students have finished their writing, we ask them to rehearse together the piece they have created with or without the score. We must give them autonomy to establish between themselves signs for the beginning and end of the music, dynamic signs and other information that they feel they need. After some rehearsals, we propose a moment of presentation of the plays to the classmates of the other group.',
    style: ['paragraph'],
  },
  {
    text:
      'We can suggest that students tell their story and then execute the play or execute the play and then tell their story, or just that they show the pictures they have chosen to create their play, depending on what we find interesting at the moment. It is also important that at this time we provide autonomy to the students in their organization and presentation to the others.',
    style: ['paragraph'],
  },
  {
    text: 'Source: Elaborated by the author.',
    style: ['paragraph', 'citation'],
  },
  {
    text: '',
    style: ['line'],
  },
  {
    text: 'Description of Game 7',
    style: ['paragraph', 'bold', 'anchor'],
  },
  {
    text: 'Improvisation with ostinato',
    style: ['title2'],
  },
  {
    text: 'Author: Various authors/Marta Brietzke',
    style: ['paragraph', 'italic'],
  },
  {
    text: 'Objectives:',
    style: ['paragraph', 'bold'],
  },
  {
    text:
      'Work the notion of solo and tutti; work the idea of ostinato; work the concept of musical form.',
    style: ['paragraph'],
  },
  {
    text: 'Methodology and application:',
    style: ['paragraph', 'bold'],
  },
  {
    text:
      'We start the improvisation game by explaining to the students what an ostinato is. Soon after, we propose some rhythmic ostinato to be performed by all students with a D open string in pizzicato.This ostinato must be simple, such as, a quarter note followed by two eighth notes.',
    style: ['paragraph'],
  },
  {
    text:
      'We then propound the students to divide the group into orchestra and soloist. The group that will represent the orchestra will perform the ostinato rhythm with reverse loose string, while the student chosen to be soloist will perform different phrases, encompassing the musical and technical knowledge they have already learned. During the solos it is not necessary that we think in harmony or vocal conduction, as for the improvisation does not need to obey the patterns of tonalism. The objective is for students to be able to freely use their musical knowledge and to organize their ideas on the basis of a rhythm in ostinato.',
    style: ['paragraph'],
  },
  {
    text:
      'We can organize improvisation in different ways, for example, by establishing that we will perform the rhythm in ostinato twice after the end of each solo, so that the next performer identifies his time to enter as a soloist in the performance. We can also arrange a improvisation in rondo form, proposing that part A be some specific number of times the repetition of the ostinato or some theme created jointly by the group, to be performed by the soloist before his free improvisation. We organize and add complexity to the game over the course of the rounds, which can be interspersed with collective listening sessions of the improvisation recording.',
    style: ['paragraph'],
  },
  {
    text: 'Source: Elaborated by the author.',
    style: ['paragraph', 'citation'],
  },
  {
    text: '',
    style: ['line'],
  },
  {
    text: 'Description of Game 8',
    style: ['paragraph', 'bold', 'anchor'],
  },
  {
    text: 'Enchanted Forest',
    style: ['title2'],
  },
  {
    text: 'Author: Claudia Freixedas',
    style: ['paragraph', 'italic'],
  },
  {
    text: 'Objectives:',
    style: ['paragraph', 'bold'],
  },
  {
    text: 'Work the musical dialogue; work the idea of polyphony.',
    style: ['paragraph'],
  },
  {
    text: 'Methodology and application:',
    style: ['paragraph', 'bold'],
  },
  {
    text:
      "It consists of a variation of the game Night at the Castle, with an addition of complexity. In this case, we also create a story in which two explorers arrive at an enchanted forest. In this forest, each tree emits a different sound. When explorers approach the trees, they emit their sound. TWe can perform this game with the eyes open or blindfolded, in a similar way to the game Night at the Castle, and the trees can emit a single sound or different sounds depending on the musical context. However, it's important that we propose some differences between this game and the previous one.",
    style: ['paragraph'],
  },
  {
    text:
      'In this game, one of the complexities to be presented to students is more than one explorer enters the forest at the same time. This feature will provide the existence of a dialogue to be "composed" by the explorers, who will also act as conductors.',
    style: ['paragraph'],
  },
  {
    text: 'Source: Elaborated by the author, based on the proposals by Claudia Freixedas.',
    style: ['paragraph', 'citation'],
  },
  {
    text: '',
    style: ['line'],
  },
  {
    text: 'Description of Game 9',
    style: ['paragraph', 'bold', 'anchor'],
  },
  {
    text: 'Zoo Tour',
    style: ['title2'],
  },
  {
    text: 'Author: Violeta Gainza',
    style: ['paragraph', 'italic'],
  },
  {
    text: 'Objectives:',
    style: ['paragraph', 'bold'],
  },
  {
    text:
      'Work on different musical options of the same sonority, work on the idea of musical narrative.',
    style: ['paragraph'],
  },
  {
    text: 'Methodology and application:',
    style: ['paragraph', 'bold'],
  },
  {
    text:
      'In this game, we also create, along with the students, a story of a visitor in a zoo. The visitor walks through the zoo and observes the different animals. Each student will choose an animal to perform with their cello. The visitor will choose which animal he wants to visit by positioning himself in front of each of the classmates who will be representing it.',
    style: ['paragraph'],
  },
  {
    text:
      "In this game, we can add the visitor's interaction with the animals. As this interaction occurs, the animal can manifest itself with different musical characters, using different dynamics, articulations, rhythms, timbristic nuances or organization of content in the form of musical phrases.",
    style: ['paragraph'],
  },
  {
    text:
      "As mentioned above, we can record the resulting improvisations, increasing the students' listening and analyzing ability, allowing that in the next rounds of the games the musical interactions are, more and more, coming from the students' artistic choice. We should always avoid that the execution of games becomes a mechanical activity and devoid of critical analysis, because in this case, we lose an important strategy of musical development of students.",
    style: ['paragraph'],
  },
  {
    text: 'Source: Elaborated by the author, based on the proposals by Violeta Gainza.',
    style: ['paragraph', 'citation'],
  },
  {
    text: '',
    style: ['line'],
  },
  {
    text: 'Description of Game 10',
    style: ['paragraph', 'bold', 'anchor'],
  },
  {
    text: 'The birds and the hunter',
    style: ['title2'],
  },
  {
    text: 'Authors: Cláudia Freixedas/Marta Brietzke',
    style: ['paragraph', 'italic'],
  },
  {
    text: 'Objectives:',
    style: ['paragraph', 'bold'],
  },
  {
    text:
      'Work on the freedom and the different options for the beginning, the development and the end of the musical "play", to work on issues related to ecology and acoustic ecology; to work on the dialogues, the polyphonies and the notion of musical form.',
    style: ['paragraph'],
  },
  {
    text: 'Methodology and application:',
    style: ['paragraph', 'bold'],
  },
  {
    text:
      'In this game, we ask students what bird sounds they know. We can collectively inventory these sounds or propose to do research in our homes and bring the results of our investigations to others. We can also bring a catalog of the bird sound recording that we can find on the internet. In doing so, we choose some birds that we wish to imitate and seek similar sounds in our cello.',
    style: ['paragraph'],
  },
  {
    text:
      "After choosing the sounds of the birds, each student becomes responsible for the emission of one of these sounds, representing a bird species. We then choose together a representative sound that emulates the sound produced by the hunter. This sound can be the sound of a shooting rifle, the hunter's walk through the woods or what the students' imagination determines.",
    style: ['paragraph'],
  },
  {
    text:
      "We suggest, then, that in the forest where the birds are found, each one of them acts in a way to dialogue with the other birds, consciously creating a sonorous content. A student will be responsible for executing the hunter's sound. All the birds, on hearing the sound of the hunter, the birds become quiet for a moment. After that, when there is no more danger, the birds gradually sing again, once again establishing a dialogue with the other birds of the forest.",
    style: ['paragraph'],
  },
  {
    text:
      "The hunter can intervene in the forest as many times as he or she wishes. We should also give autonomy so that the group feels the necessary time of silence after the hunter's intervention, the moment to start the improvisation and the moment to finish it. By doing so, we expand the group's perception of their performance and participation of each and everyone in the musical as a whole.",
    style: ['paragraph'],
  },
  {
    text: 'Source: Elaborated by the author, based on the proposals by Claudia Freixedas.',
    style: ['paragraph', 'citation'],
  },
  {
    text: '',
    style: ['line'],
  },
  {
    text: 'Suggested repertory:',
    style: ['title1', 'anchor'],
  },
  {
    text:
      'In order to contextualize the work with improvisation games within the poetics of contemporary music and offer subsidies to students, we can present, at different times, pieces from the repertoire of the 20th and 21st centuries, composed for cello. The purpose of listening to such pieces is that students can identify common elements of these pieces with their own creations and so appreciate more their work and the work of the composers presented.',
    style: ['paragraph'],
  },
  {
    text: 'List of pieces from the contemporary repertoire for cello',
    style: ['paragraph', 'bold'],
  },
  {
    text: 'Lachenmann, Helmut - Pression for solo cello',
    style: ['itemList'],
  },
  {
    text: 'ARRE! - Soundpaiting improvisations for group of cello',
    style: ['itemList'],
  },
  {
    text: 'Penderecki, Kristof - Capriccio per Siegfried Palm, for solo cello',
    style: ['itemList'],
  },
  {
    text: 'Berio, Luciano - Sequenza XIV, for solo cello',
    style: ['itemList'],
  },
  {
    text: 'Boulez, Piérre - Messagesquisse, for solo cello and six cellos',
    style: ['itemList'],
  },
  {
    text: 'Saraaiaho, Kaija - Sept Papillons, for solo cello',
    style: ['itemList'],
  },
  {
    text: 'Ligeti, Györgi - Sonata, for solo cello',
    style: ['itemList'],
  },
  {
    text: 'Romitelli, Fausto - Professor Bad Trip- lesson II- cello cadence',
    style: ['itemList'],
  },
  {
    text: 'Furrer, Beat - Solo für cello, for solo cello',
    style: ['itemList'],
  },
  {
    text: 'Scelsi, Giancinto - Trilogia, for solo cello',
    style: ['itemList'],
  },
  {
    text: 'Crumb, George - Sonata para Violoncelo, for solo cello',
    style: ['itemList'],
  },
  {
    text: 'Kagel, Maurício - Match, for two cellos and percussion',
    style: ['itemList'],
  },
  {
    text: 'Sciarrino, Salvatore - Variazoni, for solo cello and orchestr a',
    style: ['itemList'],
  },
  {
    text: 'Saunders, Rebecca - Solitude, for solo cello',
    style: ['itemList'],
  },
  {
    text: 'Rihm, Wolfgang - Von Weit, for solo cello and piano',
    style: ['itemList'],
  },
  {
    text: 'Ginastera, Alberto - Puneña, for solo cello',
    style: ['itemList'],
  },
  {
    text: 'Gras, Germán - Solo para Marta, for solo cello',
    style: ['itemList'],
  },
  {
    text: 'Kiefer, Bruno - Errância, for solo cello',
    style: ['itemList'],
  },
  {
    text: 'Santoro, Claúdio - Fantasia Sulamérica, for solo cello',
    style: ['itemList'],
  },
  {
    text: 'Nobre, Marlos - Desafio, for solo cello and orchestra',
    style: ['itemList'],
  },
  {
    text: 'Lima, Rodrigo - Circuncello, for solo cello and orchestra',
    style: ['itemList'],
  },
  {
    text: 'Ferraz, Sílvio - Lamento quase mudo, for solo cello',
    style: ['itemList'],
  },
  {
    text: 'Angelo, Bruno - Traçado íntimo e hesitante, for solo cello',
    style: ['itemList'],
  },
  {
    text: 'Source: Elaborated by the author.',
    style: ['paragraph', 'citation'],
  },
  {
    text: '',
    style: ['line'],
  },
  {
    text: 'For more information:',
    style: ['title2', 'anchor'],
  },
  {
    text: 'List of books and publications on the topics discussed',
    style: ['paragraph', 'bold'],
  },
  {
    text:
      'ALONSO, Chefa. Improvisación libre: la composición en movimiento. Baiona: Editorial Dos acordes S. L., 2007.',
    style: ['itemList'],
  },
  {
    text:
      'DELALANDE, François. La música es un juego de niños. Buenos Aires: Ricordi Americana, 2001.',
    style: ['itemList'],
  },
  {
    text:
      'COSTA, Rogério. Música errante: o jogo da improvisação livre. São Paulo: Perspectiva: Fapesp, 2016.',
    style: ['itemList'],
  },
  {
    text:
      'GAINZA, Violeta. La improvisación musical. Buenos Aires: Melos de Ricordi Americana, 2009.',
    style: ['itemList'],
  },
  {
    text:
      'KISHIMOTO, Tizuko Morchida. O jogo e a educação infantil. São Paulo: Cengage Learning, 2013.',
    style: ['itemList'],
  },
  {
    text:
      'MATTHEWS, Wade. Improvisando: La libre creación musical. Madrid: Turner Publicaciones S. L., 2012.',
    style: ['itemList'],
  },
  {
    text:
      'TOURINHO, Cristina, TOURINHO, Cristina. Ensino coletivo de instrumentos musicais: crenças, mitos, princípios e um pouco de história. In: XVI Encontro Nacional da ABEM, 2007. Mato Grosso do Sul.',
    style: ['itemList'],
  },
  {
    text: 'Source: Elaborated by the author.',
    style: ['paragraph', 'citation'],
  },
  {
    text: '',
    style: ['line'],
  },
  {
    text: 'Reference:',
    style: ['title1'],
  },
  {
    text: 'BRIETZKE, Marta Macedo. ',
    titleStrong:
      'A música contemporânea na iniciação coletiva ao violoncelo: uma pesquisa-ação com jogos de improvisação em três instituições de ensino no estado de São Paulo. ',
    rest:
      '2018. 159 f. Dissertação (Mestrado) - Escola de Comunicações e Artes - USP, São Paulo, 2018.',
    style: ['bibliography'],
  },
]

export default textData
